/**
 * Background media — play/pause for the autoplaying video in
 * sections/background-media.liquid.
 *
 * WHERE THE WORK IS SPLIT
 *
 *   Liquid — the <video> and all of its attributes (autoplay, loop, muted,
 *            playsinline, poster). The video starts playing with no JavaScript
 *            involved at all; this file only ever takes over from there.
 *   CSS    — everything about how the button looks and when it is visible. It
 *            is keyed on `data-paused` on this host element, which is the ONE
 *            state attribute here. See the button comment in the section.
 *   here   — the state itself, plus the two things it drags along: the button's
 *            `aria-label` and which of the two icons is shown.
 *
 * THE STATE COMES FROM THE VIDEO, NOT FROM US. Everything is driven by the
 * element's own `play` and `pause` events rather than set optimistically when
 * the button is clicked. That matters because a browser can pause the video
 * without asking — a blocked autoplay, a backgrounded tab, low power mode — and
 * a button reading "Pause" over a still frame is worse than no button.
 *
 * REDUCED MOTION: an autoplaying video is motion, and CLAUDE.md makes
 * `prefers-reduced-motion` a build requirement. CSS cannot stop playback, so it
 * has to happen here: the video is paused on upgrade and the visitor is left
 * looking at the poster frame with a visible play button. Nothing is hidden and
 * nothing jump-cuts — the video is simply theirs to start.
 *
 * IMAGE MODE RENDERS NO BUTTON AND NO VIDEO, so this exits immediately. The
 * custom element is still in the markup either way, which keeps the Liquid
 * simpler than conditionally naming the wrapper.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: same reason as
 * assets/site-header.js, assets/mobile-menu.js and assets/mega-menu.js — section
 * JS is bundled and injected by Shopify in an order we cannot see or control,
 * and it was measured NOT running at all on the live store.
 */
if (!customElements.get('background-media')) {
  customElements.define(
    'background-media',
    class BackgroundMedia extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('[data-media-toggle]');

        /**
         * Image mode, or a video setting the merchant has not filled in yet.
         * Nothing to wire up, and nothing rendered that needs undoing.
         */
        if (!this.video || !this.toggle) return;

        this.handleToggle = this.handleToggle.bind(this);
        this.syncState = this.syncState.bind(this);

        this.toggle.addEventListener('click', this.handleToggle);
        this.video.addEventListener('play', this.syncState);
        this.video.addEventListener('pause', this.syncState);

        /**
         * Reduced motion. `autoplay` is removed as well as pausing, because the
         * attribute alone will restart the video if the element is ever moved in
         * the DOM — which the theme editor does on every re-render.
         */
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.video.removeAttribute('autoplay');
          this.video.pause();
        }

        /**
         * The markup is rendered in the playing state, since that is what
         * autoplay produces on the overwhelming majority of loads. This corrects
         * it for the cases where it isn't — reduced motion above, or a browser
         * that refused to autoplay before this script ran.
         */
        this.syncState();
      }

      disconnectedCallback() {
        if (!this.video || !this.toggle) return;

        this.toggle.removeEventListener('click', this.handleToggle);
        this.video.removeEventListener('play', this.syncState);
        this.video.removeEventListener('pause', this.syncState);
      }

      handleToggle() {
        if (this.video.paused) {
          /**
           * `play()` returns a promise that REJECTS when the browser refuses —
           * autoplay policy, or a media resource that failed to load. Swallowing
           * it silently is fine here precisely because the UI is driven by the
           * video's own events: no `play` event fires, so the button correctly
           * stays on "Play" rather than claiming something is happening.
           */
          const played = this.video.play();
          if (played) played.catch(() => {});
        } else {
          this.video.pause();
        }
      }

      syncState() {
        const paused = this.video.paused;

        this.toggleAttribute('data-paused', paused);

        this.toggle.setAttribute(
          'aria-label',
          paused
            ? this.toggle.dataset.labelPlay
            : this.toggle.dataset.labelPause,
        );

        /**
         * Both icons are in the DOM; this only decides which one is shown. The
         * `hidden` class is Tailwind's `display: none`, matching how the markup
         * ships the play icon hidden.
         */
        const playIcon = this.toggle.querySelector('[data-icon-play]');
        const pauseIcon = this.toggle.querySelector('[data-icon-pause]');

        if (playIcon) playIcon.classList.toggle('hidden', !paused);
        if (pauseIcon) pauseIcon.classList.toggle('hidden', paused);
      }
    },
  );
}
